import React, { useState } from 'react';

// Color palette for value paths (summing)
const VALUE_COLORS = [
  { bg: 'bg-blue-200', border: 'border-blue-500', text: 'text-blue-800', name: 'blue' },
  { bg: 'bg-green-200', border: 'border-green-500', text: 'text-green-800', name: 'green' },
  { bg: 'bg-purple-200', border: 'border-purple-500', text: 'text-purple-800', name: 'purple' },
  { bg: 'bg-orange-200', border: 'border-orange-500', text: 'text-orange-800', name: 'orange' },
  { bg: 'bg-pink-200', border: 'border-pink-500', text: 'text-pink-800', name: 'pink' },
  { bg: 'bg-teal-200', border: 'border-teal-500', text: 'text-teal-800', name: 'teal' },
  { bg: 'bg-red-200', border: 'border-red-500', text: 'text-red-800', name: 'red' },
  { bg: 'bg-amber-200', border: 'border-amber-500', text: 'text-amber-800', name: 'amber' },
];

// Color palette for array filter paths (filtering)
const FILTER_COLORS = [
  { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700', name: 'indigo' },
  { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700', name: 'cyan' },
  { bg: 'bg-lime-100', border: 'border-lime-400', text: 'text-lime-700', name: 'lime' },
  { bg: 'bg-rose-100', border: 'border-rose-400', text: 'text-rose-700', name: 'rose' },
];

// Known type keys in Amazon financial data
const TYPE_KEYS = [
  'ChargeType',
  'FeeType', 
  'PromotionType',
  'DirectPaymentType',
  'AdjustmentType',
  'RetrochargeEventType',
  'RentalEventType',
  'TransactionType',
  'DebtRecoveryType',
  'EventType',
  'TaxCollectionModel',
];

/**
 * Finds the "type" key in an object (ChargeType, FeeType, etc.)
 */
function findTypeKey(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  for (const key of TYPE_KEYS) {
    if (key in obj && typeof obj[key] === 'string') {
      return { key, value: obj[key] };
    }
  }
  return null;
}

/**
 * Builds a typed path with constraints
 * typeConstraintsMap is { pathIndex: { key, value } }
 * e.g., "ShipmentEventList.[].ItemChargeList.[ChargeType=Principal].ChargeAmount.CurrencyAmount"
 */
function buildTypedPath(pathArray, typeConstraintsMap) {
  return pathArray.map((key, idx) => {
    const num = parseInt(key, 10);
    if (!isNaN(num)) {
      // This is an array index - check if there's a type constraint
      const constraint = typeConstraintsMap[idx];
      if (constraint) {
        return `[${constraint.key}=${constraint.value}]`;
      }
      return '[]';
    }
    return key;
  }).join('.');
}

/**
 * Converts a concrete path to a generic path (with [])
 * e.g., ['ShipmentEventList', '0', 'ItemChargeList', '1', 'Amount'] 
 *    -> 'ShipmentEventList.[].ItemChargeList.[].Amount'
 */
function toGenericPath(pathArray) {
  return pathArray.map(key => {
    const num = parseInt(key, 10);
    return !isNaN(num) ? '[]' : key;
  }).join('.');
}

/**
 * Checks if a value is numeric (for summing)
 */
function isNumeric(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return true;
  if (typeof value === 'string') {
    const numStr = value.replace(/[^0-9.-]/g, '');
    return numStr !== '' && !isNaN(parseFloat(numStr)) && isFinite(numStr);
  }
  return false;
}

/**
 * Extracts numeric value from various formats
 */
function extractNumericValue(value) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const numStr = value.replace(/[^0-9.-]/g, '');
    const num = parseFloat(numStr);
    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'object' && value !== null) {
    if (value.Amount !== undefined) return extractNumericValue(value.Amount);
    if (value.CurrencyAmount !== undefined) return extractNumericValue(value.CurrencyAmount);
    if (value.Value !== undefined) return extractNumericValue(value.Value);
  }
  return 0;
}

const InteractiveJSONViewer = ({ 
  data, 
  onValuePathSelect, 
  onArrayFilterSelect,
  selectedValuePaths = [], 
  selectedArrayFilters = [] 
}) => {
  const [hoveredPath, setHoveredPath] = useState(null);
  const [hoverType, setHoverType] = useState(null); // 'value' or 'array'

  // Get color index for a value path
  const getValuePathColorIndex = (typedPath) => {
    const idx = selectedValuePaths.findIndex(p => p.path === typedPath);
    return idx >= 0 ? idx % VALUE_COLORS.length : -1;
  };

  // Get color index for an array filter
  const getArrayFilterColorIndex = (genericPath) => {
    const idx = selectedArrayFilters.indexOf(genericPath);
    return idx >= 0 ? idx % FILTER_COLORS.length : -1;
  };

  /**
   * Render a value in the JSON tree
   * @param {*} value - The value to render
   * @param {string[]} path - The concrete path to this value
   * @param {Object} typeConstraintsMap - Map of path index to type constraint { key, value }
   */
  const renderValue = (value, path = [], typeConstraintsMap = {}) => {
    const typedPath = buildTypedPath(path, typeConstraintsMap);
    const genericPath = toGenericPath(path);
    
    const valueColorIndex = getValuePathColorIndex(typedPath);
    const isValueSelected = valueColorIndex >= 0;
    const valueColors = isValueSelected ? VALUE_COLORS[valueColorIndex] : null;
    
    const isHoveredValue = hoveredPath === typedPath && hoverType === 'value';
    const isNumericValue = isNumeric(value);

    const handleValueClick = (e) => {
      e.stopPropagation();
      if (isNumericValue && onValuePathSelect) {
        onValuePathSelect(typedPath);
      }
    };

    const handleValueMouseEnter = (e) => {
      e.stopPropagation();
      if (isNumericValue) {
        setHoveredPath(typedPath);
        setHoverType('value');
      }
    };

    const handleMouseLeave = () => {
      setHoveredPath(null);
      setHoverType(null);
    };

    if (value === null) {
      return <span className="text-gray-500">null</span>;
    }

    if (value === undefined) {
      return <span className="text-gray-500">undefined</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400">[]</span>;
      }
      return (
        <span>
          <span className="text-gray-600">[</span>
          <span className="text-gray-400 text-xs ml-1">({value.length} items)</span>
          <div className="ml-4">
            {value.map((item, index) => {
              // When entering an array item, check if it has a type key
              // and add it to the constraints map for this path index
              const itemPath = [...path, index.toString()];
              const itemTypeInfo = findTypeKey(item);
              
              // Build new constraints map including this item's type (if any)
              const newConstraintsMap = { ...typeConstraintsMap };
              if (itemTypeInfo) {
                // The array index is at itemPath.length - 1
                newConstraintsMap[itemPath.length - 1] = itemTypeInfo;
              }
              
              return (
                <div key={index} className="mb-1">
                  <span className="text-gray-500">{index}:</span>{' '}
                  {renderValue(item, itemPath, newConstraintsMap)}
                  {index < value.length - 1 && <span className="text-gray-600">,</span>}
                </div>
              );
            })}
          </div>
          <span className="text-gray-600">]</span>
        </span>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span className="text-gray-500">{'{}'}</span>;
      }
      
      // Check if this object has a type key (for display purposes)
      const objectTypeInfo = findTypeKey(value);
      
      return (
        <span>
          <span className="text-gray-600">{'{'}</span>
          {objectTypeInfo && (
            <span className="text-xs text-indigo-600 ml-1 font-medium">
              {objectTypeInfo.key}={objectTypeInfo.value}
            </span>
          )}
          <div className="ml-4">
            {keys.map((key, index) => {
              const childValue = value[key];
              const isArrayKey = Array.isArray(childValue);
              const childPath = [...path, key];
              const childGenericPath = toGenericPath(childPath);
              
              const arrayFilterColorIndex = isArrayKey ? getArrayFilterColorIndex(childGenericPath) : -1;
              const isArrayFilterSelected = arrayFilterColorIndex >= 0;
              const arrayFilterColors = isArrayFilterSelected ? FILTER_COLORS[arrayFilterColorIndex] : null;
              
              const isHoveredArrayKey = hoveredPath === childGenericPath && hoverType === 'array';

              const handleKeyClick = (e) => {
                if (isArrayKey && onArrayFilterSelect) {
                  e.stopPropagation();
                  onArrayFilterSelect(childGenericPath);
                }
              };

              const handleKeyMouseEnter = (e) => {
                if (isArrayKey) {
                  e.stopPropagation();
                  setHoveredPath(childGenericPath);
                  setHoverType('array');
                }
              };

              // Style differently for array keys (clickable for filtering)
              const keyClasses = isArrayKey 
                ? `cursor-pointer px-1 rounded transition-colors ${
                    isArrayFilterSelected 
                      ? `${arrayFilterColors.bg} border ${arrayFilterColors.border}` 
                      : isHoveredArrayKey 
                      ? 'bg-indigo-100 border border-indigo-300' 
                      : 'hover:bg-indigo-50'
                  }`
                : '';

              return (
                <div key={key} className="mb-1">
                  <span 
                    className={`text-blue-600 ${keyClasses}`}
                    onClick={handleKeyClick}
                    onMouseEnter={handleKeyMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    title={isArrayKey ? `Click to filter by: ${childGenericPath}` : undefined}
                  >
                    "{key}"
                    {isArrayKey && <span className="text-xs text-indigo-500 ml-1">⚡</span>}
                  </span>
                  <span className="text-gray-600">: </span>
                  {renderValue(childValue, childPath, typeConstraintsMap)}
                  {index < keys.length - 1 && <span className="text-gray-600">,</span>}
                </div>
              );
            })}
          </div>
          <span className="text-gray-600">{'}'}</span>
        </span>
      );
    }

    const baseClasses = 'px-1 rounded transition-colors';
    const clickableClasses = isNumericValue ? 'cursor-pointer' : '';
    const selectedClasses = isValueSelected 
      ? `${valueColors.bg} border-2 ${valueColors.border}` 
      : isHoveredValue 
      ? 'bg-yellow-100 border border-yellow-400' 
      : '';

    if (typeof value === 'string') {
      const displayValue = value.length > 100 ? value.substring(0, 100) + '...' : value;
      return (
        <span 
          className={`${baseClasses} ${clickableClasses} ${selectedClasses} ${isNumericValue ? 'text-green-700 font-semibold' : 'text-green-600'}`}
          onClick={handleValueClick}
          onMouseEnter={handleValueMouseEnter}
          onMouseLeave={handleMouseLeave}
          title={isNumericValue ? `Click to sum: ${typedPath}` : undefined}
        >
          "{displayValue}"
          {isNumericValue && <span className="text-xs text-green-500 ml-1">Σ</span>}
        </span>
      );
    }

    if (typeof value === 'number') {
      return (
        <span 
          className={`${baseClasses} ${clickableClasses} ${selectedClasses} text-purple-600 font-semibold`}
          onClick={handleValueClick}
          onMouseEnter={handleValueMouseEnter}
          onMouseLeave={handleMouseLeave}
          title={`Click to sum: ${typedPath}`}
        >
          {value}
          <span className="text-xs text-purple-400 ml-1">Σ</span>
        </span>
      );
    }

    if (typeof value === 'boolean') {
      return <span className="text-orange-600">{value.toString()}</span>;
    }

    return <span>{String(value)}</span>;
  };

  if (!data) {
    return <div className="text-gray-500">No data available</div>;
  }

  return (
    <div className="font-mono text-xs">
      <div className="mb-2 text-xs text-gray-600 bg-gray-100 p-2 rounded">
        <span className="font-semibold">Legend:</span>{' '}
        <span className="text-purple-600">Σ</span> = Click to sum values (type-constrained){' '}
        <span className="text-indigo-500">⚡</span> = Click to filter by array presence
      </div>
      {renderValue(data, [], {})}
    </div>
  );
};

export default InteractiveJSONViewer;
export { 
  toGenericPath, 
  buildTypedPath, 
  findTypeKey, 
  extractNumericValue, 
  isNumeric, 
  VALUE_COLORS, 
  FILTER_COLORS,
  TYPE_KEYS 
};
